import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { enqueueSnackbar } from 'notistack';

import {
  deleteAudioOverview,
  retryAudioOverview,
  toAudioOverviewError,
} from '@/services/audioOverviews';
import {
  audioOverviewRemoved,
  audioOverviewReplaced,
  audioOverviewRetryRefused,
} from '@/slices/audioOverviews/audioOverviewsSlice';
import type { AudioOverviewSummary } from '@/types/AudioOverview';
import {
  getAudioOverviewPlayer,
  type AudioOverviewPlayer,
} from '../player/audioOverviewPlayer';
import type { AudioOverviewUpgradeSource } from '../audioOverviewAnalytics';
import { audioOverviewErrorCopy } from '../audioOverviewCopy';
import {
  playAudioOverview,
  promptAudioOverviewUpgrade,
} from '../audioOverviewPlayback';
import { loadAudioOverviewConfig } from './useAudioOverviewConfig';

export interface AudioOverviewActions {
  play: (overview: AudioOverviewSummary) => void;
  /** Resolves true when the retry was accepted. */
  retry: (overview: AudioOverviewSummary) => Promise<boolean>;
  /** Resolves true when the overview is gone. */
  remove: (overview: AudioOverviewSummary) => Promise<boolean>;
  promptUpgrade: (
    source: AudioOverviewUpgradeSource,
    overview?: AudioOverviewSummary
  ) => void;
}

/** What a row, a toast or the library can do with an overview. */
export const useAudioOverviewActions = (
  player?: AudioOverviewPlayer
): AudioOverviewActions => {
  const dispatch = useDispatch();

  const promptUpgrade = useCallback(
    (source: AudioOverviewUpgradeSource, overview?: AudioOverviewSummary) =>
      promptAudioOverviewUpgrade(dispatch, source, {
        sourceKind: overview?.source,
        articleId: overview?.articleId,
      }),
    [dispatch]
  );

  const play = useCallback(
    (overview: AudioOverviewSummary) =>
      playAudioOverview(overview, { dispatch, player }),
    [dispatch, player]
  );

  const retry = useCallback(
    async (overview: AudioOverviewSummary) => {
      if (overview.locked) {
        promptUpgrade('locked_overview', overview);
        return false;
      }
      try {
        dispatch(audioOverviewReplaced(await retryAudioOverview(overview.id)));
        void loadAudioOverviewConfig(dispatch);
        return true;
      } catch (error) {
        const apiError = toAudioOverviewError(error);
        if (apiError.code === 'AUDIO_OVERVIEW_UPGRADE_REQUIRED') {
          promptUpgrade('generate_rejected', overview);
        } else if (apiError.code === 'NOT_FOUND') {
          dispatch(audioOverviewRemoved(overview.id));
        } else {
          // Hide Retry for good; the copy says to create a new one.
          if (apiError.code === 'AUDIO_OVERVIEW_NOT_RETRYABLE') {
            dispatch(audioOverviewRetryRefused(overview.id));
          }

          enqueueSnackbar(
            audioOverviewErrorCopy(apiError.code, apiError.details),
            {
              variant: 'error',
            }
          );
        }
        return false;
      }
    },
    [dispatch, promptUpgrade]
  );

  const remove = useCallback(
    async (overview: AudioOverviewSummary) => {
      if (overview.source === 'article') return false;
      try {
        await deleteAudioOverview(overview.id);
      } catch (error) {
        // Already gone (or never ours): drop it all the same.
        if (toAudioOverviewError(error).code !== 'NOT_FOUND') {
          enqueueSnackbar('Couldn’t delete this audio overview. Try again.', {
            variant: 'error',
          });

          return false;
        }
      }

      const engine = player ?? getAudioOverviewPlayer();
      if (engine.getState().track?.id === overview.id) engine.close();
      dispatch(audioOverviewRemoved(overview.id));
      void loadAudioOverviewConfig(dispatch);
      return true;
    },
    [dispatch, player]
  );

  return useMemo(
    () => ({ play, retry, remove, promptUpgrade }),
    [play, retry, remove, promptUpgrade]
  );
};
